import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { Toaster } from "sonner";

import Nav from "@/components/Nav";
import Footer from "@/components/sections/Footer";
import ProtectedRoute from "@/components/ProtectedRoute";
import SmoothScroll from "@/components/SmoothScroll";
import { AuthProvider } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import IndustriesPage from "@/pages/IndustriesPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import ProductsPage from "@/pages/ProductsPage";
import AiLabPage from "@/pages/AiLabPage";
import Contact from "@/pages/Contact";
import BlogListPage from "@/pages/BlogListPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminBlogList from "@/pages/admin/BlogList";
import AdminBlogEditor from "@/pages/admin/BlogEditor";

import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.25 } },
};

function PageWrap({ children, testId }) {
  return (
    <motion.main data-testid={testId} variants={pageVariants} initial="initial" animate="enter" exit="exit">
      {children}
    </motion.main>
  );
}

/* Public site layout — Nav + Footer + smooth scroll */
function PublicLayout() {
  return (
    <SmoothScroll>
      <div className="relative bg-bg-0 text-white min-h-screen overflow-hidden">
        <Nav />
        <Outlet />
        <Footer />
      </div>
    </SmoothScroll>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Public */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<PageWrap testId="page-home"><Home /></PageWrap>} />
          <Route path="/about" element={<PageWrap testId="page-about"><About /></PageWrap>} />
          <Route path="/services" element={<PageWrap testId="page-services"><Services /></PageWrap>} />
          <Route path="/industries" element={<PageWrap testId="page-industries"><IndustriesPage /></PageWrap>} />
          <Route path="/case-studies" element={<PageWrap testId="page-case-studies"><CaseStudiesPage /></PageWrap>} />
          <Route path="/products" element={<PageWrap testId="page-products"><ProductsPage /></PageWrap>} />
          <Route path="/ai-lab" element={<PageWrap testId="page-ai-lab"><AiLabPage /></PageWrap>} />
          <Route path="/contact" element={<PageWrap testId="page-contact"><Contact /></PageWrap>} />
          <Route path="/book-call" element={<PageWrap testId="page-book"><Contact /></PageWrap>} />
          <Route path="/blog" element={<PageWrap testId="page-blog"><BlogListPage /></PageWrap>} />
          <Route path="/blog/:slug" element={<PageWrap testId="page-blog-detail"><BlogDetailPage /></PageWrap>} />
        </Route>

        {/* Admin (no public Nav/Footer, no smooth scroll for editor perf) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}
        >
          <Route index element={<AdminBlogList />} />
          <Route path="blogs" element={<AdminBlogList />} />
          <Route path="blogs/new" element={<AdminBlogEditor />} />
          <Route path="blogs/:id/edit" element={<AdminBlogEditor />} />
        </Route>

        <Route path="*" element={<PublicLayout />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AnimatedRoutes />
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "rgba(10,10,18,0.92)",
            border: "1px solid rgba(0,240,255,0.25)",
            color: "#fff",
            backdropFilter: "blur(20px)",
          },
        }}
      />
    </div>
  );
}

export default App;
